import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import LanguageSwitcher from './components/LanguageSwitcher';
import traceMopIcon from './tracemop-icon.png';
import deviceImg from './tracemop-device-v7.png';
import dashboardImg from './tracemop-dashboard-screenshot.png';

const TEAL = '#14b8a6';
const TEAL_DIM = '#0F766E';
const TEAL_BORDER = 'rgba(20,184,166,0.22)';
const BG = '#0F172A';
const BG_ALT = '#0a0f1a';
const BG_CARD = '#1e293b';
const WHITE = '#f8fafc';
const MUTED = '#94a3b8';
const TRACE_MOP_MARK_SRC = '/trace-mop-mark.png?v=20260514';
const CONTENT_W = 1120;

function NavLink({ href, children }) {
  return (
    <a
      href={href}
      style={{ color: MUTED, fontSize: 11, fontWeight: 500, textDecoration: 'none', transition: 'color 0.15s' }}
      onMouseEnter={e => e.target.style.color = WHITE}
      onMouseLeave={e => e.target.style.color = MUTED}
    >
      {children}
    </a>
  );
}

function FeatureCard({ title, desc }) {
  return (
    <div
      className="feature-card"
      style={{
        position: 'relative',
        flex: 1,
        minWidth: 200,
        padding: '22px 22px 22px 26px',
        borderRadius: 14,
        background: `linear-gradient(155deg, rgba(20,184,166,0.10) 0%, ${BG_CARD} 42%)`,
        border: `1px solid ${TEAL_BORDER}`,
        boxShadow: '0 8px 28px rgba(0,0,0,0.22)',
        overflow: 'hidden',
      }}
    >
      <div style={{
        position: 'absolute',
        left: 0,
        top: 18,
        bottom: 18,
        width: 3,
        borderRadius: '0 3px 3px 0',
        background: `linear-gradient(180deg, ${TEAL} 0%, ${TEAL_DIM} 100%)`,
        opacity: 0.9,
      }} />
      {title ? (
        <div style={{
          fontSize: 15,
          fontWeight: 700,
          color: WHITE,
          marginBottom: 8,
          letterSpacing: '-0.2px',
        }}>{title}</div>
      ) : null}
      <div style={{ fontSize: 14, color: MUTED, lineHeight: 1.65 }}>{desc}</div>
    </div>
  );
}

function StepBadge({ n }) {
  return (
    <div style={{
      width: 44, height: 44, borderRadius: '50%',
      background: TEAL, color: '#fff',
      fontWeight: 800, fontSize: 18,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      margin: '0 auto 18px', flexShrink: 0,
    }}>{n}</div>
  );
}

const LandingPage = ({ user, setUser }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const howItWorksSteps = t('landing.howItWorks.steps', { returnObjects: true });
  const areas = t('landing.audience.areas', { returnObjects: true });
  const users = t('landing.audience.users', { returnObjects: true });
  const valueCards = t('landing.value.cards', { returnObjects: true });
  const platformItems = t('landing.dashboard.items', { returnObjects: true });
  const footerProductLinks = ['howItWorks', 'dashboard', 'audience', 'pricing', 'order'];
  const footerCompanyLinks = ['contact', 'privacy', 'terms'];
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1280
  );

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const isMobile = viewportWidth < 900;
  const isSmallMobile = viewportWidth < 640;
  const navH = isMobile ? 45 : 51;
  const sectionPad = isMobile ? '64px 20px' : '72px 48px';
  const sectionView = {
    padding: sectionPad,
    minHeight: isMobile ? 'auto' : `calc(100vh - ${navH}px)`,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    boxSizing: 'border-box',
  };

  const handleOpenApp = () => {
    navigate('/app');
  };

  const handleOrder = () => {
    navigate('/shop');
  };

  const handleOpenChat = () => {
    if (window.Tawk_API?.maximize) {
      window.Tawk_API.maximize();
      return;
    }
    if (window.$crisp) {
      window.$crisp.push(['do', 'chat:open']);
      return;
    }
    window.location.href = 'mailto:hello@tracemop.com';
  };

  const handleLogout = async () => {
    await fetch('/auth/logout', { credentials: 'include' });
    localStorage.removeItem('user');
    setUser(null);
  };

  return (
    <div style={{ fontFamily: "'Inter', system-ui, sans-serif", WebkitFontSmoothing: 'antialiased', background: BG, color: WHITE }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        .nav-link:hover { color: #f8fafc !important; }
        .app-btn:hover { background: ${TEAL} !important; color: #fff !important; }
        .cta-btn:hover { background: #0d9488 !important; }
        .outline-btn:hover { background: rgba(255,255,255,0.07) !important; }
        .feature-card { transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s; }
        .feature-card:hover {
          border-color: rgba(20,184,166,0.45) !important;
          box-shadow: 0 12px 36px rgba(20,184,166,0.12), 0 8px 28px rgba(0,0,0,0.28) !important;
          transform: translateY(-2px);
        }
      `}</style>

      {/* ── NAV (−20% méret) ── */}
      <nav id="top" className={`fixed top-0 left-0 right-0 ${isMobile ? 'h-[45px]' : 'h-[51px]'} bg-slate-900 text-slate-100 flex items-center px-0 z-[150] shadow-md border-b border-slate-800`}>
        <div className={`${isMobile ? 'w-[45px] h-[45px]' : 'w-[51px] h-[51px]'} flex items-center justify-center shrink-0 border-r border-slate-800`}>
          <img
            src={TRACE_MOP_MARK_SRC}
            alt="Trace Mop"
            className={`${isMobile ? 'h-[26px]' : 'h-8'} w-auto`}
            draggable={false}
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = traceMopIcon;
            }}
          />
        </div>
        <div className={`${isMobile ? 'ml-1.5 text-[13px]' : 'ml-2.5 text-base'} font-bold tracking-tight select-none text-slate-100`}>
          Trace Mop
        </div>

        <div className="mx-auto flex gap-[26px] items-center" style={{ display: isMobile ? 'none' : 'flex' }}>
          <NavLink href="#audience">{t('common.nav.audience')}</NavLink>
          <NavLink href="#value">{t('common.nav.value')}</NavLink>
          <NavLink href="#how-it-works">{t('common.nav.howItWorks')}</NavLink>
          <NavLink href="#dashboard">{t('common.nav.dashboard')}</NavLink>
          <NavLink href="/shop">{t('common.nav.shop')}</NavLink>
        </div>

        <div className={`flex items-center ${isMobile ? 'gap-1.5 pr-1.5' : 'gap-2.5 pr-3'}`}>
          <LanguageSwitcher compact={isSmallMobile} scale={0.8} />
          <button
            className={`app-btn ${isSmallMobile ? 'text-[8px] px-1.5 py-0.5' : 'text-[10px] px-3 py-1.5'} font-semibold rounded border border-teal-500 text-teal-400 hover:bg-teal-500 hover:text-white transition`}
            onClick={handleOpenApp}
          >
            {t('common.appButton')}
          </button>
          <div
            className={`${isSmallMobile ? 'w-[26px] h-[26px]' : 'w-[29px] h-[29px]'} rounded-full bg-slate-800 border border-slate-700 overflow-hidden flex items-center justify-center cursor-pointer`}
            onClick={handleLogout}
            title={t('common.signOutTitle', { name: user?.name || user?.email })}
          >
            {user?.photo_url ? (
              <img src={user.photo_url} alt="avatar" className="w-full h-full object-cover" draggable={false} />
            ) : user?.picture ? (
              <img src={user.picture} alt="avatar" className="w-full h-full object-cover" draggable={false} />
            ) : (
              <span className="text-[10px] font-semibold text-slate-100">
                {(user?.name || user?.email || 'U').trim()[0]?.toUpperCase()}
              </span>
            )}
          </div>
        </div>
      </nav>

      {/* ── HERO — tartalom magasságához igazítva, nincs 100vh üres tér ── */}
      <section style={{
        display: 'flex',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent: 'space-between',
        flexDirection: isMobile ? 'column' : 'row',
        padding: isMobile
          ? '72px 20px 36px'
          : `${navH + 28}px 48px 48px`,
        maxWidth: CONTENT_W,
        margin: '0 auto',
        gap: isMobile ? 28 : 40,
        boxSizing: 'border-box',
      }}>
        <div style={{
          flex: '1.15 1 0',
          maxWidth: isMobile ? '100%' : 560,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          minHeight: isMobile ? 'auto' : 420,
        }}>
          <h1 style={{
            fontSize: 'clamp(34px, 4vw, 48px)', fontWeight: 800,
            letterSpacing: '-2px', lineHeight: 1.1, color: WHITE, marginBottom: 14,
          }}>
            {t('landing.hero.titleBefore')}<br />
            <span style={{ color: TEAL }}>{t('landing.hero.titleAccent')}</span>
          </h1>

          <p style={{
            fontSize: isMobile ? 15 : 16,
            color: MUTED,
            lineHeight: 1.55,
            marginBottom: 8,
            maxWidth: 520,
          }}>
            {t('landing.hero.subtitle')}
          </p>

          <p style={{
            fontSize: isMobile ? 15 : 16,
            color: MUTED,
            lineHeight: 1.55,
            marginBottom: isMobile ? 22 : 26,
            maxWidth: 520,
          }}>
            {t('landing.hero.body')}
          </p>

          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
            <button
              className="cta-btn"
              onClick={handleOrder}
              style={{
                background: TEAL, border: 'none',
                borderRadius: 8, padding: '12px 24px',
                fontSize: 14, fontWeight: 700, color: '#fff',
                cursor: 'pointer', fontFamily: 'inherit',
                transition: 'background 0.15s',
              }}
            >
              {t('landing.hero.cta')}
            </button>
          </div>
        </div>

        <div style={{
          flex: '0.95 1 0',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          maxWidth: isMobile ? '100%' : 480,
          height: isMobile ? 'auto' : 420,
        }}>
          <img
            src={deviceImg}
            alt={t('landing.hero.deviceAlt')}
            style={{
              height: isMobile ? 'auto' : 420,
              maxHeight: isMobile ? 280 : 420,
              maxWidth: '100%',
              width: 'auto',
              objectFit: 'contain',
              filter: 'drop-shadow(0 24px 48px rgba(20,184,166,0.18))',
            }}
          />
        </div>
      </section>

      {/* ── KINEK SZÓL ── */}
      <section id="audience" style={sectionView}>
        <div style={{ maxWidth: CONTENT_W, margin: '0 auto', width: '100%' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <p style={{ fontSize: 12, letterSpacing: '2.5px', textTransform: 'uppercase', color: TEAL, fontWeight: 600, marginBottom: 12 }}>{t('landing.audience.label')}</p>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, letterSpacing: '-1.5px', color: WHITE }}>
              {t('landing.audience.title')}
            </h2>
          </div>

          <p style={{ fontSize: 15, fontWeight: 600, color: '#94a3b8', marginBottom: 16, textAlign: 'center', lineHeight: 1.5 }}>
            {t('landing.audience.areasLabel')}
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center', marginBottom: 48 }}>
            {areas.map((item) => (
              <span key={item} style={{
                fontSize: 14, color: MUTED, padding: '8px 16px',
                border: `1px solid ${TEAL_BORDER}`, borderRadius: 999,
              }}>{item}</span>
            ))}
          </div>

          <p style={{ fontSize: 15, fontWeight: 600, color: '#94a3b8', marginBottom: 20, textAlign: 'center', lineHeight: 1.5 }}>
            {t('landing.audience.usersLabel')}
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: 16,
          }}>
            {users.map((item) => (
              <FeatureCard key={item} desc={item} />
            ))}
          </div>
        </div>
      </section>

      {/* ── ÜZLETI ÉRTÉK ── */}
      <section id="value" style={{ ...sectionView, background: BG_ALT }}>
        <div style={{ maxWidth: CONTENT_W, margin: '0 auto', width: '100%' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <p style={{ fontSize: 12, letterSpacing: '2.5px', textTransform: 'uppercase', color: TEAL, fontWeight: 600, marginBottom: 12 }}>{t('landing.value.label')}</p>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, letterSpacing: '-1.5px', color: WHITE }}>
              {t('landing.value.title')}
            </h2>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: 16,
          }}>
            {valueCards.map(({ title, body }) => (
              <FeatureCard key={title} title={title} desc={body} />
            ))}
          </div>
        </div>
      </section>

      {/* ── HOGYAN MŰKÖDIK ── */}
      <section id="how-it-works" style={sectionView}>
        <div style={{ maxWidth: CONTENT_W, margin: '0 auto', width: '100%' }}>
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <p style={{ fontSize: 12, letterSpacing: '2.5px', textTransform: 'uppercase', color: TEAL, fontWeight: 600, marginBottom: 12 }}>{t('landing.howItWorks.label')}</p>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, letterSpacing: '-1.5px', color: WHITE }}>
              {t('landing.howItWorks.title')}
            </h2>
          </div>
          <div style={{ display: 'flex', gap: isMobile ? 28 : 40, flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row' }}>
            {howItWorksSteps.map(({ title, desc }, index) => (
              <div key={title} style={{ flex: 1, minWidth: 200, textAlign: 'center', padding: '0 8px' }}>
                <StepBadge n={index + 1} />
                <div style={{ fontSize: 17, fontWeight: 700, color: WHITE, marginBottom: 12 }}>{title}</div>
                <div style={{ fontSize: 14, color: MUTED, lineHeight: 1.75 }}>{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TRACKER ── */}
      <section id="tracker" style={{ ...sectionView, background: BG_ALT }}>
        <div style={{ maxWidth: CONTENT_W, margin: '0 auto', width: '100%' }}>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <p style={{ fontSize: 12, letterSpacing: '2.5px', textTransform: 'uppercase', color: TEAL, fontWeight: 600, marginBottom: 12 }}>{t('landing.tracker.label')}</p>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, letterSpacing: '-1.5px', color: WHITE }}>
              {t('landing.tracker.title')}
            </h2>
          </div>

          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: 'center',
            gap: isMobile ? 32 : 48,
          }}>
            <div style={{
              flex: 1,
              width: '100%',
              maxWidth: isMobile ? '100%' : 480,
              display: 'flex',
              justifyContent: 'center',
            }}>
              <img
                src={deviceImg}
                alt={t('landing.tracker.imageAlt')}
                style={{
                  width: '100%',
                  maxHeight: isMobile ? 280 : 340,
                  objectFit: 'contain',
                  borderRadius: 14,
                  filter: 'drop-shadow(0 20px 40px rgba(20,184,166,0.14))',
                }}
              />
            </div>
            <div style={{ flex: 1, maxWidth: isMobile ? '100%' : 480 }}>
              <p style={{ fontSize: isMobile ? 15 : 16, color: MUTED, lineHeight: 1.75 }}>
                {t('landing.tracker.body')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── CONTROL CENTER ── */}
      <section id="dashboard" style={sectionView}>
        <div style={{ maxWidth: CONTENT_W, margin: '0 auto', width: '100%' }}>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <p style={{ fontSize: 12, letterSpacing: '2.5px', textTransform: 'uppercase', color: TEAL, fontWeight: 600, marginBottom: 12 }}>{t('landing.dashboard.label')}</p>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, letterSpacing: '-1.5px', color: WHITE }}>
              {t('landing.dashboard.title')}
            </h2>
          </div>

          <img
            src={dashboardImg}
            alt={t('landing.dashboard.screenshotAlt')}
            style={{
              width: '100%',
              display: 'block',
              borderRadius: 14,
              border: '1px solid rgba(255,255,255,0.08)',
              boxShadow: '0 0 80px rgba(20,184,166,0.10)',
              marginBottom: 48,
            }}
          />

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            columnGap: 48,
            rowGap: 28,
          }}>
            {platformItems.map(({ title, body }) => (
              <div key={title}>
                <div style={{ fontSize: 15, fontWeight: 700, color: WHITE, marginBottom: 8 }}>{title}</div>
                <div style={{ fontSize: 14, color: MUTED, lineHeight: 1.7 }}>{body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CLEANER APP ── */}
      <section id="cleaner-app" style={{ ...sectionView, background: BG_ALT }}>
        <div style={{ maxWidth: CONTENT_W, margin: '0 auto', width: '100%' }}>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <p style={{ fontSize: 12, letterSpacing: '2.5px', textTransform: 'uppercase', color: TEAL, fontWeight: 600, marginBottom: 12 }}>{t('landing.cleanerApp.label')}</p>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, letterSpacing: '-1.5px', color: WHITE }}>
              {t('landing.cleanerApp.title')}
            </h2>
          </div>

          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: 'center',
            gap: isMobile ? 32 : 48,
          }}>
            <div style={{ flex: 1, maxWidth: isMobile ? '100%' : 520 }}>
              <p style={{ fontSize: isMobile ? 15 : 16, color: MUTED, lineHeight: 1.75 }}>
                {t('landing.cleanerApp.body')}
              </p>
            </div>
            <div style={{
              flex: 1,
              width: '100%',
              maxWidth: isMobile ? '100%' : 480,
              minHeight: isMobile ? 220 : 300,
              borderRadius: 14,
              border: `1px solid ${TEAL_BORDER}`,
              background: `linear-gradient(155deg, rgba(20,184,166,0.08) 0%, ${BG_CARD} 50%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#64748b',
              fontSize: 14,
              fontWeight: 600,
              letterSpacing: '0.5px',
            }}
              aria-label={t('landing.cleanerApp.imageAlt')}
            >
              {t('landing.cleanerApp.imagePlaceholder')}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section style={{
        ...sectionView,
        background: `linear-gradient(135deg, ${TEAL_DIM} 0%, ${TEAL} 100%)`,
        textAlign: 'center',
      }}>
        <div style={{ maxWidth: CONTENT_W, margin: '0 auto', width: '100%' }}>
        <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, letterSpacing: '-1.5px', color: '#fff', marginBottom: 16 }}>
          {t('landing.cta.title')}
        </h2>
        <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.82)', marginBottom: 36, maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
          {t('landing.cta.body')}
        </p>
        <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            className="outline-btn"
            onClick={handleOrder}
            style={{
              background: '#fff', border: 'none',
              borderRadius: 8, padding: '14px 32px',
              fontSize: 15, fontWeight: 700, color: TEAL_DIM,
              cursor: 'pointer', fontFamily: 'inherit',
              transition: 'background 0.15s',
            }}
          >
            {t('landing.cta.order')}
          </button>
          <button
            type="button"
            onClick={handleOpenChat}
            style={{
              background: 'rgba(255,255,255,0.15)',
              border: '1px solid rgba(255,255,255,0.4)',
              borderRadius: 8, padding: '14px 32px',
              fontSize: 15, fontWeight: 600, color: '#fff',
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            {t('landing.cta.chat')}
          </button>
        </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{ background: '#07090f', padding: sectionPad }}>
        <div style={{ maxWidth: CONTENT_W, margin: '0 auto' }}>
          <div style={{ display: 'flex', gap: isMobile ? 32 : 80, flexWrap: 'wrap', marginBottom: 48, flexDirection: isMobile ? 'column' : 'row' }}>
            <div style={{ flex: 2, minWidth: 200 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <img src={traceMopIcon} alt="" style={{ height: 32, width: 32, filter: 'invert(1) hue-rotate(180deg) saturate(1.4)' }} />
                <span style={{ fontSize: 16, fontWeight: 800, color: WHITE }}>Trace Mop</span>
              </div>
              <p style={{ fontSize: 13, color: MUTED, lineHeight: 1.7, maxWidth: 260 }}>
                {t('common.footer.tagline')}
              </p>
            </div>

            <div style={{ flex: 1, minWidth: 140 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: 16 }}>{t('common.footer.product')}</div>
              {footerProductLinks.map((key) => {
                const href = key === 'pricing' || key === 'order'
                  ? '/shop'
                  : `#${key === 'howItWorks' ? 'how-it-works' : key}`;
                return (
                <div key={key} style={{ marginBottom: 10 }}>
                  <a href={href} style={{ fontSize: 13, color: MUTED, textDecoration: 'none' }}
                    onMouseEnter={e => e.target.style.color = WHITE}
                    onMouseLeave={e => e.target.style.color = MUTED}
                  >{t(`common.footer.links.${key}`)}</a>
                </div>
                );
              })}
            </div>

            <div style={{ flex: 1, minWidth: 140 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: 16 }}>{t('common.footer.company')}</div>
              {footerCompanyLinks.map((key) => (
                <div key={key} style={{ marginBottom: 10 }}>
                  <a href="mailto:hello@tracemop.com" style={{ fontSize: 13, color: MUTED, textDecoration: 'none' }}
                    onMouseEnter={e => e.target.style.color = WHITE}
                    onMouseLeave={e => e.target.style.color = MUTED}
                  >{t(`common.footer.links.${key}`)}</a>
                </div>
              ))}
            </div>
          </div>

          <div style={{ borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <span style={{ fontSize: 12, color: '#475569' }}>{t('common.footer.copyright')}</span>
            <span style={{ fontSize: 12, color: '#475569' }}>{t('common.footer.domain')}</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
